import { ColumnDef } from '@tanstack/react-table';
import { ItemCollection } from '@/app/types/itemCollection';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';

export const editCollectionEvent = 'editCollection';
export const deleteCollectionEvent = 'deleteCollection';

export const columns: ColumnDef<ItemCollection>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => {
      return (
        <div className="font-medium">
          {row.original.name}
        </div>
      );
    },
  },
  {
    accessorKey: 'slug',
    header: 'Slug',
    cell: ({ row }) => {
      return (
        <code className="text-sm bg-muted px-2 py-1 rounded">
          {row.original.slug}
        </code>
      );
    },
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) => {
      const description = row.original.description;
      return description ? (
        <span className="text-sm text-muted-foreground">
          {description.length > 50 ? `${description.substring(0, 50)}...` : description}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground italic">No description</span>
      );
    },
  },
  {
    accessorKey: 'items',
    header: 'Items Count',
    cell: ({ row }) => {
      const items = row.original.items;
      return (
        <Badge variant="secondary">
          {items?.length || 0} items
        </Badge>
      );
    },
  },
  {
    accessorKey: 'is_active',
    header: 'Status',
    cell: ({ row }) => {
      const isActive = row.original.is_active;
      return (
        <Badge variant={isActive ? 'success' : 'secondary'}>
          {isActive ? 'Active' : 'Inactive'}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => {
      return format(new Date(row.original.created_at), 'MMM dd, yyyy');
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const collection = row.original;

      const handleEdit = () => {
        const event = new CustomEvent(editCollectionEvent, {
          detail: { collection },
        });
        document.dispatchEvent(event);
      };

      const handleDelete = () => {
        const event = new CustomEvent(deleteCollectionEvent, {
          detail: { collection },
        });
        document.dispatchEvent(event);
      };

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleDelete}
              className="text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];